import type { CanvasEdge } from "./studio.js";
import type { PromptBinding, PromptContent, PromptSource, ReferenceKind } from "./prompt-content.js";
import { canvasDocumentV3Schema, type CanvasDocumentV3, type CanvasNodeV3 } from "./studio-v3.js";

export interface ResolvedMediaReference {
  edgeId: string; nodeId: string; assetId: string; kind: ReferenceKind;
  mention: `@图片${number}` | `@视频${number}` | `@音频${number}`;
  role: CanvasEdge["role"]; mode?: CanvasEdge["mode"]; instruction: string; order: number;
}
export interface ReferenceBindingDiagnostic {
  code: "UNBOUND_REFERENCE" | "REMOVED_REFERENCE" | "REPLACED_ASSET" | "INVALID_BINDING" | "INVALID_GRAPH";
  message: string; targetNodeId: string; source?: PromptSource; slotId?: string; edgeIds?: string[];
}
export interface ResolvedTargetPrompt {
  finalPrompt: string; references: ResolvedMediaReference[]; diagnostics: ReferenceBindingDiagnostic[]; ready: boolean;
}

const kindLabels = { image: "图片", video: "视频", audio: "音频" } as const;
const diagnosticMessages = {
  UNBOUND_REFERENCE: "引用尚未绑定，请选择参考素材。",
  REMOVED_REFERENCE: "引用的素材节点或连线已移除，请重新绑定。",
  REPLACED_ASSET: "引用的素材已被替换，请确认后重新绑定。",
  INVALID_BINDING: "引用绑定与当前提示词来源或素材身份不一致。",
  INVALID_GRAPH: "画布结构或输入节点无效，请检查节点和连线。"
} as const;
const bindingKey = (source: PromptSource, slotId: string): string => JSON.stringify([source.nodeId, source.field, source.inputEdgeId, slotId]);
const isMedia = (node: CanvasNodeV3 | undefined): node is Extract<CanvasNodeV3, { kind: ReferenceKind }> =>
  node?.kind === "image" || node?.kind === "video" || node?.kind === "audio";
const compareEdges = (a: CanvasEdge, b: CanvasEdge): number => a.order - b.order || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);

/** Owns a private detached graph; no authorization decisions are cached here. */
export function createReferenceResolutionContextV3(input: CanvasDocumentV3) {
  const document = structuredClone(input);
  const parsed = canvasDocumentV3Schema.safeParse(document);
  const issues = parsed.success ? [] : parsed.error.issues;
  const invalidStructure = issues.some(issue => issue.code !== "custom" || issue.params?.referenceBinding !== true);
  const nodes = new Map(invalidStructure ? [] : document.nodes.map(node => [node.id, node] as const));
  const incomingByTarget = new Map<string, CanvasEdge[]>();
  const outgoing = new Set<string>();
  if (!invalidStructure) {
    for (const edge of document.edges) {
      outgoing.add(edge.sourceNodeId);
      const edges = incomingByTarget.get(edge.targetNodeId) ?? [];
      edges.push(edge);
      incomingByTarget.set(edge.targetNodeId, edges);
    }
    for (const edges of incomingByTarget.values()) edges.sort(compareEdges);
  }
  const results = new Map<string, ResolvedTargetPrompt>();
  const resolve = (targetNodeId: string): ResolvedTargetPrompt => {
    let result = results.get(targetNodeId);
    if (!result) {
      result = resolveSnapshot(targetNodeId);
      results.set(targetNodeId, result);
    }
    return structuredClone(result);
  };
  function resolveSnapshot(targetNodeId: string): ResolvedTargetPrompt {
    const diagnostics: ReferenceBindingDiagnostic[] = [];
    const invalidKeys = new Set<string>();
    const diagnose = (code: ReferenceBindingDiagnostic["code"], extra: Omit<ReferenceBindingDiagnostic, "code" | "message" | "targetNodeId"> = {}): void => {
      diagnostics.push({ code, message: diagnosticMessages[code], targetNodeId, ...extra });
    };
    const invalidGraph = (): ResolvedTargetPrompt => {
      diagnose("INVALID_GRAPH");
      return { finalPrompt: "", references: [], diagnostics, ready: false };
    };
    if (invalidStructure) return invalidGraph();
    if (!parsed.success) {
      for (const issue of issues) {
        if (issue.code !== "custom" || issue.params?.referenceBinding !== true) return invalidGraph();
        if (issue.params.targetNodeId === targetNodeId) {
          const source = issue.params.source as PromptSource;
          const slotId = issue.params.slotId as string;
          invalidKeys.add(bindingKey(source, slotId));
          diagnose("INVALID_BINDING", { source, slotId });
        }
      }
    }
    const target = nodes.get(targetNodeId);
    if (target?.kind !== "seedance" || outgoing.has(targetNodeId)) return invalidGraph();
    const incoming = incomingByTarget.get(targetNodeId) ?? [];
    const counts = { image: 0, video: 0, audio: 0 };
    const references: ResolvedMediaReference[] = [];
    for (const edge of incoming) {
      const source = nodes.get(edge.sourceNodeId);
      if (isMedia(source)) {
        const mention = `@${kindLabels[source.kind]}${++counts[source.kind]}` as ResolvedMediaReference["mention"];
        references.push({ edgeId: edge.id, nodeId: source.id, assetId: source.assetId, kind: source.kind, mention,
          role: edge.role, ...(edge.mode === undefined ? {} : { mode: edge.mode }), instruction: edge.instruction, order: edge.order });
      } else if (source?.kind !== "text" && source?.kind !== "script") return invalidGraph();
    }
    const referencesByEdge = new Map(references.map((reference) => [reference.edgeId, reference]));
    const bindings = new Map(target.referenceBindings.map((binding) => [bindingKey(binding.source, binding.slotId), binding]));
    const reported = new Set(invalidKeys);
    const render = (prompt: PromptContent, source: PromptSource): string => prompt.segments.map((segment) => {
      if (segment.type === "text") return segment.text;
      const key = bindingKey(source, segment.slotId);
      const binding: PromptBinding | undefined = bindings.get(key);
      let code: ReferenceBindingDiagnostic["code"] = "UNBOUND_REFERENCE";
      if (invalidKeys.has(key)) code = "INVALID_BINDING";
      else if (binding?.value.state === "bound") {
        const identity = binding.value.target;
        const reference = referencesByEdge.get(identity.edgeId);
        if (reference && reference.nodeId === identity.nodeId && reference.assetId === identity.assetId && reference.kind === identity.kind && reference.kind === segment.kind) return reference.mention;
        code = "INVALID_BINDING";
      } else if (binding?.value.state === "missing") {
        const reason = binding.value.reason;
        code = reason === "asset_replaced" ? "REPLACED_ASSET" : reason === "edge_removed" || reason === "node_removed" ? "REMOVED_REFERENCE" : "UNBOUND_REFERENCE";
      }
      if (!reported.has(key)) {
        diagnose(code, { source, slotId: segment.slotId });
        reported.add(key);
      }
      return `【待绑定：${segment.label}】`;
    }).join("");
    const fragments = [render(target.settings.prompt, { nodeId: targetNodeId, field: "prompt", inputEdgeId: null }).trim()];
    for (const edge of incoming) {
      const source = nodes.get(edge.sourceNodeId)!;
      if (source.kind === "text") fragments.push(render(source.text, { nodeId: source.id, field: "text", inputEdgeId: edge.id }).trim());
      if (source.kind === "script") {
        const fields = ["title", "synopsis"] as const;
        fragments.push(fields.map((field) => render(source[field], { nodeId: source.id, field, inputEdgeId: edge.id })).filter(Boolean).join("\n").trim());
      }
    }
    return { finalPrompt: fragments.filter(Boolean).join("\n\n"), references, diagnostics, ready: diagnostics.length === 0 };
  }
  return Object.freeze({ resolve });
}

/** Identity/type consistency only. Callers MUST separately authorize every connected asset. */
export function resolveTargetReferencesV3(document: CanvasDocumentV3, targetNodeId: string): ResolvedTargetPrompt {
  return createReferenceResolutionContextV3(document).resolve(targetNodeId);
}
